import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { ORDER_LIST_FILTERS } from './orders-list-filters'
import { OrdersPeriodField } from './orders-period-field'
import type { OrderListValues } from './orders-list-filters'
import { FilterField } from '@/components/common/FilterField'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

// Thời gian có ô riêng (OrdersPeriodField); các ô còn lại đều mang giá trị chuỗi.
type FieldKey = Exclude<keyof typeof ORDER_LIST_FILTERS.fields, 'period'>

// Ít dùng (telemetry 08/2026: gần như không ai chạm) → gom dưới "Lọc thêm".
const MORE_KEYS = [
    'einvoice_status',
    'settlement_intent',
    'sla_state',
    'has_lot_debt',
    'kiotviet_unlinked',
    'quick_sale',
    'shipping_carrier_id',
] as const satisfies readonly FieldKey[]

interface Props {
    value: OrderListValues
    patch: (partial: Partial<OrderListValues>) => void
    surface: 'bar' | 'sheet'
}

/**
 * Bộ lọc danh sách đơn xếp dọc như KiotViet: thời gian → trạng thái → thanh
 * toán → kênh/người tạo → ngày giao → "Lọc thêm". Cùng một panel cho sidebar
 * desktop (áp ngay) và bottom sheet mobile (bản nháp, bấm Áp dụng).
 */
export function OrdersFilterPanel({ value, patch, surface }: Props) {
    const moreActive = MORE_KEYS.filter(
        (key) => value[key] !== ORDER_LIST_FILTERS.defaults[key],
    ).length
    const [moreOpen, setMoreOpen] = useState(moreActive > 0)
    const showMore = moreOpen || moreActive > 0

    const field = (key: FieldKey) => {
        const def = ORDER_LIST_FILTERS.fields[key]
        return (
            <FilterField key={key} label={def.label()}>
                {(id) =>
                    def.render({
                        id,
                        value: value[key],
                        // Khoá động nên TS không suy được kiểu — cùng cách ép với
                        // use-list-filters.
                        onChange: (next: string) =>
                            patch({ [key]: next } as Partial<OrderListValues>),
                        surface,
                    })
                }
            </FilterField>
        )
    }
    const group = (key: 'status' | 'payment_status') => (
        <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">
                {ORDER_LIST_FILTERS.fields[key].label()}
            </legend>
            {ORDER_LIST_FILTERS.fields[key].render({
                id: `orders-filter-${key}-${surface}`,
                value: value[key],
                onChange: (next) =>
                    patch({ [key]: next } as Partial<OrderListValues>),
                surface,
            })}
        </fieldset>
    )

    return (
        <div className="grid content-start gap-5">
            <OrdersPeriodField value={value} patch={patch} surface={surface} />
            {group('status')}
            {group('payment_status')}
            {field('source_channel')}
            {field('created_by')}
            <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">
                    {m.order_delivered_period_label()}
                </legend>
                <div
                    className={twJoin(
                        'grid gap-2',
                        surface === 'sheet' ? 'grid-cols-2' : 'grid-cols-1',
                    )}
                >
                    {field('delivered_from')}
                    {field('delivered_to')}
                </div>
            </fieldset>
            <div className="grid gap-3">
                <button
                    type="button"
                    aria-expanded={showMore}
                    onClick={() => setMoreOpen(!showMore)}
                    className="focus-visible:outline-ring flex items-center gap-1 text-left text-sm font-medium focus-visible:outline-2"
                >
                    <ChevronDown
                        className={twJoin(
                            'size-4 transition-transform',
                            !showMore && '-rotate-90',
                        )}
                    />
                    {m.order_filter_more()}
                    {moreActive > 0 ? (
                        <span className="bg-primary text-primary-foreground ml-1 rounded-full px-1.5 text-xs tabular-nums">
                            {moreActive}
                        </span>
                    ) : null}
                </button>
                {showMore ? (
                    <div className="grid gap-3">{MORE_KEYS.map(field)}</div>
                ) : null}
            </div>
        </div>
    )
}
